import React, { useRef, useCallback, useState, useEffect, useMemo } from 'react';
import { CalendarFilled, DeleteOutlined } from '@ant-design/icons';
import { DatePicker } from 'antd';
import _ from 'lodash';
import cx from 'classnames';
import moment from 'moment';

import { defaultEndedOnRangeDate } from './common/index';
import { getFilterConfig } from './selector';
import FilterItem from '../filter-report-item';

import style from './index.less';

const { RangePicker } = DatePicker;

const DateRange = props => {
  const { maxMinDate, value, onChange, renderExtraFooter, dateProps = {}, timeRanges, maxDays, ...restProps } = props;
  const [currentDate, setCurrentDate] = useState();

  const { max: maxDate, min: minDate } = maxMinDate || {};
  const disabledDate = useCallback(
    date => {
      if (!date) return true;
      if (!maxDate && !minDate) return false;

      let min = moment(minDate);
      let max = moment(maxDate);
      if (maxDays && currentDate) {
        const currentMin = currentDate.clone().subtract(maxDays - 1, 'day');
        const currentMax = currentDate.clone().add(maxDays, 'day');
        min = currentMin.isAfter(min) ? currentMin : min;
        max = currentMax.isBefore(max) ? currentMax : max;
      }

      try {
        return min.valueOf() > date.valueOf() || date.valueOf() >= max.valueOf();
      } catch (e) {
        return true;
      }
    },
    [minDate, maxDate, currentDate]
  );

  const pickerRef = useRef();

  const clearHoverValue = () => {
    // done breaking change
    pickerRef.current?.picker?.clearHoverValue();
  };

  useEffect(() => {
    if (dateProps.showTime) {
      clearHoverValue();
    }
  }, [value]);

  const onCalendarChange = date => {
    if (!maxDays) return;
    setCurrentDate(date.length === 1 ? date[0] : undefined);
  };

  return (
    <RangePicker
      ref={pickerRef}
      renderExtraFooter={renderExtraFooter ? renderExtraFooter : null}
      suffixIcon={<CalendarFilled />}
      allowClear={false}
      disabledDate={disabledDate}
      className={`${style.picker_range}`}
      ranges={timeRanges ? timeRanges : ranges}
      format={dateProps.showTime ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY/M/D'}
      value={value}
      onChange={onChange}
      {...dateProps}
      {...restProps}
      onCalendarChange={onCalendarChange}
    />
  );
};

const ranges = {
  ['今天']: [moment().startOf('day'), moment().endOf('day')],
  ['昨天']: [
    moment()
      .subtract(1, 'day')
      .startOf('day'),
    moment()
      .subtract(1, 'day')
      .endOf('day')
  ],
  ['过去7天']: [
    moment()
      .startOf('day')
      .subtract(7, 'day'),
    moment()
      .endOf('day')
      .subtract(1, 'day')
  ],
  ['过去30天']: [
    moment()
      .startOf('day')
      .subtract(30, 'day'),
    moment()
      .endOf('day')
      .subtract(1, 'day')
  ]
};

const FilterGroup = React.forwardRef((props, _ref) => {
  const {
    configSettingData,
    onParamsChange,
    filterDisabled,
    defaultRangeDate,
    includeDateRange = true,
    className,
    dateProps = {},
    externalFilterChange = false, // 外部控制filter的变化
    onResetFilter,
    maxDays
  } = props;

  const {
    filterData = [],
    oriData = [],
    maxMinDate,
    queryFieldConfigList = [],
    conditionConfig = {},
    defaultConfig,
    operator,
    renderExtraFooter,
    timeRanges,
    clearAllCache = false
  } = configSettingData;
  const [timeParams, setTimeParam] = useState(null);
  const [searchParams, setSearchParams] = useState([]);
  const [checkedConfig, setCheckConfig] = useState([]);
  const [itemsStatus, setItemsStatus] = useState({});
  const [isTimeChange, setIsTimeChange] = useState(false);
  const [timeValue, setTimeValue] = useState(defaultRangeDate ? defaultRangeDate : defaultEndedOnRangeDate);
  const defaultRangeRef = useRef(defaultRangeDate ? defaultRangeDate : defaultEndedOnRangeDate);
  const searchParamRef = useRef([]);

  const setSearchRef = data => (searchParamRef.current = data || []);

  //时间改变
  useEffect(() => {
    if (defaultRangeDate) {
      dateChange(
        defaultRangeDate,
        [moment(defaultRangeDate[0], 'YYYY/MM/DD').format('YYYY/MM/DD'), moment(defaultRangeDate[1], 'YYYY/MM/DD').format('YYYY/MM/DD')],
        true
      );
    }
  }, [defaultRangeDate]);

  const configData = useMemo(
    () => getFilterConfig(queryFieldConfigList, conditionConfig, filterData, oriData),
    [queryFieldConfigList, conditionConfig, filterData, oriData]
  );

  const preDate = useRef();
  const currentTimeParam = useRef();
  /**
   * 时间处理
   * @param {} e
   * @param {*} data
   */
  const dateChange = (e, data, isSetDefault) => {
    if (!data[0] || !data[1]) return setTimeParam([]);
    if (!isSetDefault) {
      setIsTimeChange(true);
    }
    setTimeValue(e);
    let min;
    let max;

    if (dateProps?.showTime) {
      min = e[0].valueOf();
      max = e[1].valueOf();
    } else {
      min = e[0].clone().startOf('day');
      min = new Date(min).getTime();
      max = e[1].clone().endOf('day');
      max = new Date(max).getTime();
    }

    const ctimeParam = [
      {
        column: defaultConfig.column,
        operator: 'gt',
        parameters: [min] // 筛选开始时间戳
      },
      {
        column: defaultConfig.column,
        operator: 'le',
        parameters: [max] // 筛选开始时间戳
      }
    ];
    currentTimeParam.current = ctimeParam;
    setTimeParam(ctimeParam);
  };

  const clearFilter = () => {
    onResetFilter && onResetFilter();
    setCheckConfig([]);
    setSearchParams([]);
    setSearchRef();
    setTimeValue(defaultRangeRef.current);
    setIsTimeChange(false);
    dealDefaultParams(defaultRangeRef.current);
    Object.keys(refs.current).forEach(item => {
      refs.current[item].clear && refs.current[item].clear();
    });
  };

  const clearFilterItem = useCallback(
    value => {
      const data = checkedConfig.filter(x => x.value != value);

      setCheckConfig(data);
      const index = searchParams.findIndex(x => x.value === value || x.key === value || x.column === value);
      if (index > -1) {
        const tSearch = _.clone(searchParams);
        tSearch.splice(index, 1);
        setSearchParams(tSearch);
        setSearchRef(tSearch);
      }
    },
    [checkedConfig, searchParams]
  );

  const filterChange = useCallback(
    (data, conditionData, key, column) => {
      const param = { key, data, conditionData, column };

      const t = _.clone(searchParamRef.current);
      const index = t.findIndex(x => x.key === key || x.value === key);
      if (index < 0) {
        t.push(param);
        setSearchRef(t);
        return setSearchParams(t);
      }
      t.splice(index, 1, param);
      setSearchRef(t);
      setSearchParams(t);
    },
    [] // 内部只读 searchParamRef.current，无需依赖 searchParams state
  );

  const dealDefaultParams = range => {
    const start = new Date(range[0]).getTime();
    const last = new Date(range[1]).getTime();
    const param = [
      {
        column: defaultConfig.column,
        operator: 'gt',
        parameters: [start] // 筛选开始时间戳
      },
      {
        column: defaultConfig.column,
        operator: 'le',
        parameters: [last] // 筛选开始时间戳
      }
    ];

    setTimeParam(param);
    currentTimeParam.current = param;
  };

  useEffect(() => {
    dealDefaultParams(defaultRangeRef.current);
  }, []);

  const getParam = () => {
    const param = [];

    searchParams.forEach(item => {
      const t = {
        column: item.column,
        parameters: item.data,
        operator: 'eq'
      };

      if (item.conditionData && item.conditionData.value) {
        t.operator = operator[item.conditionData.value];
      }
      param.push(t);
    });
    return param;
  };

  const firstLoad = useRef(false);
  useEffect(() => {
    if (timeParams === null) return;
    const param = getParam();
    if ((!dateProps.showTime && onParamsChange) || !firstLoad.current) {
      onParamsChange([...timeParams, ...param]);
      firstLoad.current = true;
    }
  }, [timeParams, searchParams]);

  useEffect(() => {
    if (!dateProps.showTime || timeParams === null) return;
    const param = getParam();
    onParamsChange([...timeParams, ...param]);
  }, [searchParams]);

  const itemVisibleChange = useCallback(
    (visible, val) => {
      setItemsStatus(prev => ({ ...prev, [val.value]: visible }));
    },
    [] // 用 functional update，不再依赖 itemsStatus，保持稳定引用
  );

  const refs = useRef({});
  const hasClickOk = useRef(false);

  const onOk = () => {
    if (!dateProps.showTime) return;
    hasClickOk.current = true;
    const param = getParam();
    onParamsChange([...currentTimeParam.current, ...param]);
  };

  const onOpenChange = bool => {
    if (!dateProps.showTime) return;
    if (!hasClickOk.current && !bool) {
      setTimeValue(preDate.current);
    }
    if (bool) {
      preDate.current = timeValue;
    }
    hasClickOk.current = false;
  };

  return (
    <div id='filter-box' className={cx(style['filter-box'], className)}>
      <div className={style.filterWrap} style={{ gap: 8 }}>
        {includeDateRange ? (
          <DateRange
            showOk={false}
            timeRanges={timeRanges}
            dateProps={dateProps}
            maxMinDate={maxMinDate}
            onChange={dateChange}
            value={timeValue}
            renderExtraFooter={renderExtraFooter}
            onOk={onOk}
            onOpenChange={onOpenChange}
            maxDays={maxDays}
          />
        ) : null}
        {configData.length > 0 &&
          configData.map((val, index) => (
            <FilterItem
              clearAllCache={clearAllCache}
              filterDisabled={filterDisabled}
              {...val}
              ref={x => (refs.current[index] = x)}
              key={index}
              clearFilterItem={clearFilterItem}
              filterChange={filterChange}
              itemVisibleChange={visible => itemVisibleChange(visible, val)}
            />
          ))}
        {searchParams.length || isTimeChange || externalFilterChange ? (
          <div className={style.clearBtn} onClick={clearFilter}>
            <DeleteOutlined className={style.clearIcon} /> {'清除筛选条件'}
          </div>
        ) : null}
      </div>
    </div>
  );
});

export default FilterGroup;
